import React, { useEffect, useState } from 'react';
import { DownloadSimple, AndroidLogo, ShieldCheck, FileArrowDown } from '@phosphor-icons/react';
import { useI18n } from '../contexts/I18nContext';

// APK + manifest are served by the VM (Caddy) at `<backend>/downloads/...`,
// NOT by Firebase Hosting. This keeps the Firebase free-tier egress untouched
// regardless of how many caregivers install the app. The base URL is whatever
// the rest of the app uses to reach the backend, with any trailing `/api`
// stripped because `/downloads/` is a sibling of `/api/` in the Caddy config.
const RAW_BASE = (process.env.REACT_APP_E2CARE_API || process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const BACKEND_ORIGIN = RAW_BASE.replace(/\/api$/, '');
const DOWNLOADS_BASE = `${BACKEND_ORIGIN}/downloads`;

/**
 * Public APK landing page. Reads `<backend>/downloads/latest.json` for the
 * current build metadata, then exposes a single download button. No auth — the
 * URL is meant to be shareable so caregivers can install directly.
 */
export default function Download() {
  const { t } = useI18n();
  const [meta, setMeta] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    fetch(`${DOWNLOADS_BASE}/latest.json?ts=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setMeta)
      .catch(() => setLoadErr(true));
  }, []);

  const apkHref = meta?.file ? `${DOWNLOADS_BASE}/${meta.file}` : null;
  const sizeMb = typeof meta?.size_bytes === 'number'
    ? (meta.size_bytes / (1024 * 1024)).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-cream text-ink flex flex-col" data-testid="download-page">
      {/* --- Header --- */}
      <header className="px-6 sm:px-10 py-6 flex items-center gap-3 border-b border-stone_line/60 bg-white/60 backdrop-blur">
        <img src="/favicon.png" alt="E2 Care" className="w-9 h-9 rounded-lg" />
        <div className="font-heading text-xl text-ink leading-none">
          E2 Care
          <div className="text-[11px] uppercase tracking-[0.2em] text-sage-500 font-semibold mt-1">
            {t('dl_companion_app')}
          </div>
        </div>
      </header>

      {/* --- Hero --- */}
      <main className="flex-1 flex items-center justify-center px-6 sm:px-10 py-12">
        <div className="max-w-xl w-full">
          <div className="flex flex-col items-center text-center">
            <div className="w-24 h-24 rounded-3xl bg-sage-100 grid place-items-center mb-6 shadow-soft">
              <img src="/favicon.png" alt="" className="w-14 h-14" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-tight">
              {t('dl_hero_title')}
            </h1>
            <p className="text-ink-soft mt-4 max-w-md">
              {t('dl_hero_sub')}
            </p>

            {/* --- Download card --- */}
            <div
              data-testid="download-card"
              className="mt-10 w-full bg-white border border-stone_line rounded-2xl shadow-soft p-6 sm:p-7 text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-sage-100 text-sage-700 grid place-items-center shrink-0">
                  <AndroidLogo size={26} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-ink">{t('dl_android_apk')}</div>
                  <div className="text-xs text-ink-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {meta?.version ? (
                      <span data-testid="download-version">v{meta.version}</span>
                    ) : (
                      <span className="text-ink-muted/70">{t('dl_version_loading')}</span>
                    )}
                    {meta?.released_at && (
                      <span data-testid="download-released-at">{t('dl_released', { date: meta.released_at })}</span>
                    )}
                    {sizeMb && <span>{sizeMb} MB</span>}
                    {meta?.min_android && <span>{t('dl_android_min', { version: meta.min_android })}</span>}
                  </div>
                </div>
              </div>

              <a
                href={apkHref || '#'}
                download
                aria-disabled={!apkHref}
                data-testid="download-apk-link"
                onClick={(e) => { if (!apkHref) e.preventDefault(); }}
                className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-base font-medium transition-colors ${
                  apkHref
                    ? 'bg-sage text-white hover:bg-sage-700'
                    : 'bg-stone_line text-ink-muted cursor-not-allowed'
                }`}
              >
                <DownloadSimple size={20} weight="bold" />
                {apkHref ? t('dl_download_apk') : (loadErr ? t('dl_unavailable') : t('dl_preparing'))}
              </a>

              {loadErr && (
                <p className="mt-3 text-xs text-terracotta-700" data-testid="download-error">
                  {t('dl_manifest_error')}
                </p>
              )}

              {/* --- Changelog --- */}
              {Array.isArray(meta?.changelog) && meta.changelog.length > 0 && (
                <div className="mt-5 pt-4 border-t border-stone_line">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
                    {t('dl_whats_new')}
                  </div>
                  <ul className="space-y-1.5" data-testid="download-changelog">
                    {meta.changelog.map((line, i) => (
                      <li key={i} className="text-sm text-ink-soft flex gap-2">
                        <span className="text-sage-600 mt-1.5 shrink-0">·</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* --- Install instructions --- */}
            <div className="mt-10 w-full text-left bg-bg-alt border border-stone_line rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 text-sage-700 font-medium mb-3">
                <ShieldCheck size={18} weight="duotone" />
                {t('dl_installing_title')}
              </div>
              <ol className="space-y-2 text-sm text-ink-soft list-decimal list-inside marker:text-sage-600">
                <li>{t('dl_step1')}</li>
                <li>{t('dl_step2')}</li>
                <li>{t('dl_step3')}</li>
                <li>{t('dl_step4')}</li>
              </ol>
              <div className="mt-4 text-xs text-ink-muted flex items-start gap-2">
                <FileArrowDown size={14} className="mt-0.5 shrink-0" />
                <span>{t('dl_iphone_note')} <a href="/" className="text-sage-700 hover:underline">e2-care.web.app</a>.</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* --- Footer --- */}
      <footer className="px-6 sm:px-10 py-5 text-xs text-ink-muted text-center border-t border-stone_line/60">
        © {new Date().getFullYear()} E2 Care · <a href="/" className="hover:text-sage-700">{t('dl_back_to_dashboard')}</a>
      </footer>
    </div>
  );
}
